import { PageController } from '@client/components/_core/layout/PageController';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import type { RouterOutputs } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { formatDateTime } from '@shared/utils/core/formatting';
import { createFileRoute } from '@tanstack/react-router';
import { Collapse, Descriptions, Drawer, Spin, Table, Tag } from 'antd';
import { useMemo } from 'react';

export const Route = createFileRoute('/admin-console/ai/eval-runs/$evalRunId')({
    params: {
        parse({ evalRunId }) {
            return { evalRunId };
        },
    },
    staticData: {
        presentation: 'overlay' as const,
    },
    component: EvalRunDetail,
});

const statusTagColor: Record<string, string> = {
    RUNNING: 'blue',
    COMPLETED: 'green',
    FAILED: 'red',
    CANCELLED: 'default',
};

const resultStatusTagColor: Record<string, string> = {
    PASSED: 'green',
    FAILED: 'red',
    ERROR: 'orange',
    PENDING: 'default',
};

const resultStatusLabel: Record<string, string> = {
    PASSED: 'Passed',
    FAILED: 'Failed',
    ERROR: 'Error',
    PENDING: 'Pending',
};

type EvalResult = RouterOutputs['aiEval']['getEvalRun']['Results'][number];

function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
}

function EvalRunDetail() {
    const navigate = useNavigate();
    const { evalRunId } = Route.useParams();
    const { trpc } = useTrpc();

    const {
        data: run,
        isLoading,
        isFetching,
        error,
        refetch,
    } = trpc.aiEval.getEvalRun.useQuery({ id: BigInt(evalRunId) });
    const hasBlockingError = hasBlockingQueryError(error, run);

    const handleClose = () => {
        void navigate({ to: '/admin-console/ai/eval-runs' });
    };

    const passRate = run
        ? run.totalTestCases > 0
            ? Math.round((run.passedTestCases / run.totalTestCases) * 100)
            : 0
        : 0;

    const promptSnapshot = run?.promptSnapshot as Record<string, unknown> | null;

    const resultColumns = useMemo(
        () => [
            {
                title: '#',
                dataIndex: 'testCaseIndex',
                key: 'testCaseIndex',
                width: 50,
                render: (_: unknown, record: EvalResult) => record.testCaseIndex + 1,
            },
            {
                title: 'Name',
                dataIndex: 'testCaseName',
                key: 'testCaseName',
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (_: unknown, record: EvalResult) => {
                    return (
                        <Tag color={resultStatusTagColor[record.status] ?? 'default'} variant="outlined">
                            {resultStatusLabel[record.status] ?? record.status}
                        </Tag>
                    );
                },
            },
            {
                title: 'Tokens',
                key: 'tokens',
                width: 80,
                render: (_: unknown, record: EvalResult) => record.totalTokens?.toLocaleString() ?? '-',
            },
            {
                title: 'Duration',
                key: 'duration',
                width: 80,
                render: (_: unknown, record: EvalResult) =>
                    record.executionTimeMs ? formatDuration(record.executionTimeMs) : '-',
            },
        ],
        [],
    );

    const expandedRowRender = (record: EvalResult) => {
        const snapshot = record.testCaseSnapshot as Record<string, unknown> | null;
        return (
            <div className="space-y-3 p-2">
                <div>
                    <div className="mb-1 font-medium text-muted-foreground text-xs">Input</div>
                    <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-xs">
                        {JSON.stringify(snapshot?.input ?? {}, null, 2)}
                    </pre>
                </div>
                <div>
                    <div className="mb-1 font-medium text-muted-foreground text-xs">Expected output</div>
                    <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-xs">
                        {JSON.stringify(snapshot?.expectedOutput ?? null, null, 2)}
                    </pre>
                </div>
                <div>
                    <div className="mb-1 font-medium text-muted-foreground text-xs">Actual output</div>
                    <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-xs">
                        {JSON.stringify(record.actualOutput ?? null, null, 2)}
                    </pre>
                </div>
                {record.errorMessage && (
                    <div>
                        <div className="mb-1 font-medium text-red-500 text-xs dark:text-red-400">Error</div>
                        <pre className="max-h-20 overflow-auto rounded bg-red-50 p-2 text-red-700 text-xs dark:bg-red-950/30 dark:text-red-300">
                            {record.errorMessage}
                        </pre>
                    </div>
                )}
            </div>
        );
    };

    const user = run?.TriggeredBy?.User;
    const triggeredByName = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '-' : '-';

    return (
        <PageController route={Route}>
            <Drawer
                open
                onClose={handleClose}
                afterOpenChange={(open) => {
                    if (!open) handleClose();
                }}
                title={
                    run ? (
                        <Space>
                            <span>{run.name || run.promptKey}</span>
                            <Tag color={statusTagColor[run.status] ?? 'default'} variant="outlined">
                                {run.status}
                            </Tag>
                        </Space>
                    ) : (
                        'Eval run'
                    )
                }
                width={720}
                destroyOnHidden
            >
                {hasBlockingError ? (
                    <InlineQueryError
                        error={error}
                        onRetry={() => void refetch()}
                        retrying={isFetching}
                        title="Couldn't load eval run"
                    />
                ) : isLoading ? (
                    <div className="flex justify-center py-12">
                        <Spin />
                    </div>
                ) : run ? (
                    <div className="space-y-6">
                        {error && (
                            <InlineQueryError
                                error={error}
                                onRetry={() => void refetch()}
                                retrying={isFetching}
                                title="Couldn't refresh eval run"
                                compact
                            />
                        )}

                        {/* Summary */}
                        <Descriptions size="small" column={2} bordered>
                            <Descriptions.Item label="Prompt key">
                                <span className="font-mono text-xs">{run.promptKey}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Version">v{run.promptVersion}</Descriptions.Item>
                            <Descriptions.Item label="Provider / Model">
                                {run.provider} / {run.model}
                            </Descriptions.Item>
                            <Descriptions.Item label="Pass rate">
                                <span
                                    className={
                                        passRate === 100
                                            ? 'text-green-600'
                                            : passRate >= 50
                                              ? 'text-orange-500'
                                              : 'text-red-500'
                                    }
                                >
                                    {run.passedTestCases}/{run.totalTestCases} ({passRate}%)
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Total tokens">
                                {(run.totalPromptTokens + run.totalCompletionTokens).toLocaleString()}
                            </Descriptions.Item>
                            <Descriptions.Item label="Duration">
                                {run.totalExecutionTimeMs > 0 ? formatDuration(run.totalExecutionTimeMs) : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Triggered by">{triggeredByName}</Descriptions.Item>
                            <Descriptions.Item label="Created">
                                {run.createdAt ? formatDateTime(new Date(run.createdAt), 'yyyy-MM-dd HH:mm:ss') : '-'}
                            </Descriptions.Item>
                            {run.isBaseline && (
                                <Descriptions.Item label="Baseline">
                                    <Tag color="gold" variant="outlined">
                                        Baseline
                                    </Tag>
                                </Descriptions.Item>
                            )}
                        </Descriptions>

                        {/* Prompt snapshot */}
                        {promptSnapshot && (
                            <Collapse
                                size="small"
                                items={[
                                    {
                                        key: 'snapshot',
                                        label: 'Prompt snapshot',
                                        children: (
                                            <pre className="max-h-60 overflow-auto text-xs">
                                                {JSON.stringify(promptSnapshot, null, 2)}
                                            </pre>
                                        ),
                                    },
                                ]}
                            />
                        )}

                        {/* Results table */}
                        <div>
                            <div className="mb-2 font-medium text-sm">Results</div>
                            <Table
                                dataSource={run.Results}
                                columns={resultColumns}
                                rowKey="id"
                                size="small"
                                pagination={false}
                                expandable={{
                                    expandedRowRender,
                                    rowExpandable: () => true,
                                }}
                            />
                        </div>
                    </div>
                ) : null}
            </Drawer>
        </PageController>
    );
}
